import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createRoot } from "react-dom/client";

import Button from "../Buttons/Button";

const toastContainer = document.createElement("div");
document.body.appendChild(toastContainer);
const root = createRoot(toastContainer);

let toasts = [];
let counter = 0;

const ToastContainer = ({ position = "top-right", children }) => {
  const positionClasses = {
    "top-right": "top-4 right-4 flex flex-col items-end",
    "top-left": "top-4 left-4 flex flex-col items-start",
    "bottom-right": "bottom-4 right-4 flex flex-col-reverse items-end",
    "bottom-left": "bottom-4 left-4 flex flex-col-reverse items-start",
    "top-center": "top-4 left-1/2 -translate-x-1/2 flex flex-col items-center",
    "bottom-center":
      "bottom-4 left-1/2 -translate-x-1/2 flex flex-col-reverse items-center",
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-[100]`}>
      <AnimatePresence mode="popLayout">{children}</AnimatePresence>
    </div>
  );
};

const Toast = React.forwardRef(
  (
    {
      message,
      type = "success",
      onClose,
      variant = "text",
      size = "md",
      duration = 3000,
      showProgress = true,
      pauseOnHover = true,
      position = "top-right",
      icon,
    },
    ref
  ) => {
    const [isPaused, setIsPaused] = React.useState(false);
    const [progress, setProgress] = React.useState(100);
    const progressInterval = React.useRef(null);
    const timeoutRef = React.useRef(null);
    const pausedTimeRef = React.useRef(0);
    const startTimeRef = React.useRef(Date.now());

    const sizeStyles = {
      xs: "min-w-[200px] text-xs",
      "xs-sm": "min-w-[220px] text-sm",
      sm: "min-w-[250px] text-sm",
      md: "min-w-[270px] text-base leading-[22px]",
      lg: "min-w-[300px] text-base",
      xl: "min-w-[350px] text-base",
    };

    const variants = {
      contained: {
        primary: "bg-primary text-white",
        success: "bg-success text-white",
        warning: "bg-warning text-white",
        danger: "bg-danger text-white",
        info: "bg-info text-white",
        black: "bg-black text-white",
        white: "bg-white text-gray-800 border border-gray-200",
      },
      outlined: {
        primary: "bg-white border-[1px] border-l-4 border-primary text-primary",
        success: "bg-white border-[1px] border-l-4 border-success text-success",
        warning: "bg-white border-[1px] border-l-4 border-warning text-warning",
        danger: "bg-white border-[1px] border-l-4 border-danger text-danger",
        info: "bg-white border-[1px] border-l-4 border-info text-info",
        black: "bg-white border-[1px] border-l-4 border-black text-black",
        white:
          "bg-white text-gray-800  border-[1px] border-l-4   border-gray-200",
      },
      text: {
        primary: "bg-primary-50 text-primary",
        success: "bg-success-50 text-success",
        warning: "bg-warning-50 text-warning",
        danger: "bg-danger-50 text-danger",
        info: "bg-info-50 text-info",
        black: "bg-gray-100 text-black",
        white: "bg-white text-gray-800 border border-gray-200",
      },
    };

    const iconSizes = {
      xs: "text-[22px]",
      "xs-sm": "text-[22px]",
      sm: "text-[22px]",
      md: "text-[22px]",
      lg: "text-[22px]",
      xl: "text-[22px]",
    };

    const types = {
      success: {
        icon: (
          <span className={`material-symbols-rounded ${iconSizes[size]}`}>
            check_circle
          </span>
        ),
        color: "success",
      },
      error: {
        icon: (
          <span className={`material-symbols-rounded ${iconSizes[size]}`}>
            error
          </span>
        ),
        color: "danger",
      },
      warning: {
        icon: (
          <span className={`material-symbols-rounded ${iconSizes[size]}`}>
            warning
          </span>
        ),
        color: "warning",
      },
      info: {
        icon: (
          <span className={`material-symbols-rounded ${iconSizes[size]}`}>
            info
          </span>
        ),
        color: "info",
      },
      primary: {
        icon: (props) =>
          props.icon ? (
            <span
              className={`material-symbols-rounded ${iconSizes[props.size]}`}
            >
              {props.icon}
            </span>
          ) : null,
        color: "primary",
      },
      black: {
        icon: (props) =>
          props.icon ? (
            <span
              className={`material-symbols-rounded ${iconSizes[props.size]}`}
            >
              {props.icon}
            </span>
          ) : null,
        color: "black",
      },
      white: {
        icon: (props) =>
          props.icon ? (
            <span
              className={`material-symbols-rounded ${iconSizes[props.size]}`}
            >
              {props.icon}
            </span>
          ) : null,
        color: "white",
      },
    };

    // Get the color from the type definition or default to primary
    const toastColor = types[type]?.color || "primary";

    const progressBarColors = {
      contained: {
        primary: "bg-white/50",
        success: "bg-white/50",
        warning: "bg-white/50",
        danger: "bg-white/50",
        info: "bg-white/50",
        black: "bg-white/50",
        white: "bg-black/30",
      },
      outlined: {
        primary: "bg-primary-100",
        success: "bg-success-100",
        warning: "bg-warning-100",
        danger: "bg-danger-100",
        info: "bg-info-100",
        black: "bg-black/50",
        white: "bg-black/10",
      },
      text: {
        primary: "bg-primary-200",
        success: "bg-success-200",
        warning: "bg-warning-200",
        danger: "bg-danger-200",
        info: "bg-info-200",
        black: "bg-black/20",
        white: "bg-black/10",
      },
    };

    React.useEffect(() => {
      if (!duration) return;

      const startTimer = () => {
        const remainingTime = duration - pausedTimeRef.current;
        startTimeRef.current = Date.now();

        timeoutRef.current = setTimeout(() => {
          onClose();
        }, remainingTime);

        if (showProgress) {
          progressInterval.current = setInterval(() => {
            const elapsed =
              Date.now() - startTimeRef.current + pausedTimeRef.current;
            const remaining = Math.max(0, duration - elapsed);
            const progressPercent = (remaining / duration) * 100;

            if (progressPercent <= 0) {
              clearInterval(progressInterval.current);
            } else {
              setProgress(progressPercent);
            }
          }, 10);
        }
      };

      if (!isPaused) {
        startTimer();
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          pausedTimeRef.current += Date.now() - startTimeRef.current;
        }
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
        }
      };
    }, [duration, isPaused, onClose, showProgress]);

    const handleMouseEnter = React.useCallback(() => {
      if (pauseOnHover) {
        setIsPaused(true);
      }
    }, [pauseOnHover]);

    const handleMouseLeave = React.useCallback(() => {
      if (pauseOnHover) {
        setIsPaused(false);
      }
    }, [pauseOnHover]);

    return (
      <motion.div
        layout
        initial={{
          opacity: 0,
          y: position.includes("bottom") ? 20 : -20,
          scale: 0.9,
        }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
        transition={{ duration: 0.2 }}
        className={`
        ${variants[variant][toastColor]}
        ${sizeStyles[size]}
        shadow-lg rounded-md mb-2 relative overflow-hidden
      `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center gap-1 p-3 py-2 pl-2">
          {types[type] && typeof types[type].icon === "function"
            ? types[type].icon({ size, icon })
            : types[type]?.icon}
          <p className="flex-1">{message}</p>
          <Button
            type="icon"
            variant="text"
            size={size === "xs" ? "xs" : "sm"}
            icon="close"
            color={variant !== "contained" ? "black" : "white"}
            onClick={() => onClose()}
          />
        </div>
        {/* {showProgress && (
          <motion.div
            className={`absolute bottom-0 left-0 h-1 ${progressBarColors[variant][toastColor]}`}
            initial={{ width: "100%" }}
            animate={{ width: `${progress}%` }}
            //transition={{ duration: 0.3, ease: "linear" }}
            transition={{ duration: duration / 10000, ease: "linear" }}
          />
        )} */}
        {showProgress && (
          <motion.div
            className={`absolute bottom-0 left-0 h-1 ${progressBarColors[variant][toastColor]}`}
            initial={{ width: "100%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.01, ease: "linear" }}
          />
        )}
      </motion.div>
    );
  }
);

const renderToasts = () => {
  const groupedToasts = toasts.reduce((acc, toast) => {
    const position = toast.position || "top-right";
    if (!acc[position]) acc[position] = [];
    acc[position].push(toast);
    return acc;
  }, {});

  root.render(
    <>
      {Object.entries(groupedToasts).map(([position, positionToasts]) => (
        <ToastContainer key={position} position={position}>
          {positionToasts.map((toast) => (
            <Toast
              key={toast.id}
              {...toast}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </ToastContainer>
      ))}
    </>
  );
};

export const removeToast = (id) => {
  const toastToRemove = toasts.find((toast) => toast.id === id);
  const position = toastToRemove?.position || "top-right";
  toasts = toasts.filter((toast) => toast.id !== id);
  renderToasts(position);
};

export default Toast;

export const showToast = (
  message,
  type,
  duration,
  variant,
  size,
  options = {}
) => {
  const id = counter++;
  const {
    showProgress = true,
    pauseOnHover = true,
    position = "top-right",
    icon = null,
  } = options;

  const newToast = {
    id,
    message,
    type,
    variant,
    size,
    duration,
    showProgress,
    pauseOnHover,
    position,
    icon,
  };

  toasts = [newToast, ...toasts];
  renderToasts(position);
};

/*
Usage Guide for Toast Component

1. Basic Usage:
```javascript
import { showToast } from './components/Toast/Toast';

// Simple success toast
showToast('Operation successful!');

// Error toast with custom duration
showToast('Error occurred', 'error', 5000);

// Warning toast with custom variant and size
showToast('Warning message', 'warning', 3000, 'outlined', 'md');
```

2. Advanced Usage with Options:
```javascript
showToast(
  'Custom toast with all options',
  'info',                // type: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'black' | 'white'
  4000,                  // duration in ms
  'text',                // variant: 'contained' | 'outlined' | 'text'
  'lg',                  // size: 'xs' | 'xs-sm' | 'sm' | 'md' | 'lg' | 'xl'
  {
    showProgress: true,  // show/hide progress bar
    pauseOnHover: true,  // pause timer on hover
    position: 'top-right', // 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
    icon: 'custom_icon'  // optional custom icon name (material symbols)
  }
);
```

3. Removing Toasts Programmatically:
```javascript
import { removeToast } from './components/Toast/Toast';

// Remove a specific toast by ID (ID is returned by showToast)
const toastId = showToast('This toast will be removed');
removeToast(toastId);
```

Default Values:
- type: 'success'
- duration: 3000ms
- variant: 'contained'
- size: 'xs-sm'
- options:
  - showProgress: true
  - pauseOnHover: true
  - position: 'top-right'
  - icon: null
*/
